import { useState, useEffect, useReducer, useContext } from "react";
import { Button, Card, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import DataGrid from "../../components/dataGrid/DataGrid";

import { Link, useLocation, useNavigate } from "react-router-dom";
import QueryString from "qs";
import moment from "moment";
import { Check, Edit, Plus } from "react-feather";
import { getPendingInvoices, updatePendingInvoice } from "../../services/pendingInvoice.service";
import { Formik, getIn } from "formik";
import FormInput from "../../components/FormInput";
import { validationSchema } from "../../validations/pendingInvoice.validation";
import { CompanyContext } from "../../contexts/CompanyContext";
import FormSelect from "../../components/FormSelect";
import { getParties } from "../../services/parties.service";
import { formatIndianAmount } from "../../utils/numberFormat";

const initialState = {
  data: [],
  page: 1,
  limit: 100,
  totalPages: 1,
  totalResults: 0,
  startIndex: 1,
  endIndex: 100,
};

function reducer(state: any, action: any) {
  switch (action.type) {
    case "SET_DATA":
      return action.payload;
    default:
      return state;
  }
}

function PendingInvoice() {
  const [filterData, setFilterData] = useState({ partyName: "", orderNumber: "" });
  const [party, setParty] = useState("");
  const [loading, setLoading] = useState(true);
  const [data, dispatch] = useReducer(reducer, initialState);
  const [modal, setModal] = useState(false);
  const [invoice, setInvoice] = useState<any>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { selectedCompany }: any = useContext(CompanyContext);

  const toggle = () => setModal(!modal);

  const setData = (data: any) => {
    dispatch({ type: "SET_DATA", payload: data });
  };

  const columns = [
    {
      selector: (row: any) => row.serialNumber,
      name: "S.No.",
      sortable: false,
      //   wrap: true,
      maxWidth: "70px",
      minWidth: "70px",
    },

    {
      selector: (row: any) => row.party?.name || "",
      name: "Party Name",
      sortable: false,
      wrap: true,
    },
    {
      selector: (row: any) => row.numberOfBox,
      name: "Box",
      sortable: false,
      wrap: true,
    },
    {
      selector: (row: any) => row.lineItem,
      name: "Item",
      sortable: false,
      wrap: true,
    },

    {
      selector: (row: any) => row.orderNumber,
      name: "Order No.",
      sortable: false,
      wrap: true,
    },
    {
      selector: (row: any) => moment(row.orderDate).format("DD MMMM YYYY"),
      name: "Order Date",
      sortable: false,
      wrap: true,
    },
    {
      selector: (row: any) => formatIndianAmount(row.orderAmount),
      name: "Order Amount",
      sortable: false,
      wrap: true,
    },

    {
      selector: (row: any) => {
        return <>{row.VOR ? <Check strokeWidth={3} className='text-success' /> : ""}</>;
      },
      name: "VOR",
      sortable: false,
      wrap: true,
      maxWidth: "65px",
      minWidth: "65px",
    },
    {
      selector: (row: any) => (
        <>
          <Link
            className='me-1'
            to={`edit/${row.id}?${QueryString.stringify({
              redirect: location.pathname + location.search,
            })}`}
          >
            <Edit size={15} />
          </Link>
          <Plus
            className='text-primary'
            style={{ cursor: "pointer" }}
            size={17}
            strokeWidth={2.5}
            onClick={() => {
              setInvoice(row);
              toggle();
            }}
          />
        </>
      ),
      name: "Actions",
      sortable: false,
      center: true,
      maxWidth: "90px",
      minWidth: "90px",
    },
  ];

  useEffect(() => {
    const { page, party, orderNumber } = query;
    setLoading(true);
    getPendingInvoices({ page, party, orderNumber })
      .then((response: any) => {
        setData(response.data);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  }, [location.search]);

  const handleFilter = () => {
    const queryParams: any = { ...query, page: 1, ...filterData };

    Object.keys(queryParams).forEach((key) => {
      if (!queryParams[key]) {
        delete queryParams[key];
      }
    });

    const queryString = QueryString.stringify(queryParams);
    navigate(`${location.pathname}${Object.keys(queryParams).length > 0 ? "?" + queryString : ""}`);
  };
  const handleClearFilter = () => {
    setParty("");
    setFilterData({ partyName: "", orderNumber: "" });
    const queryParams = { page: 1 };
    const queryString = QueryString.stringify(queryParams);
    navigate(`${location.pathname}?${queryString}`);
  };

  return (
    <div className='d-flex flex-column h-100 px-sm-1 w-100 pb-sm-1'>
      <Row className='align-items-center mb-2 page-title-row'>
        <Col sm>
          <h1 className='my-1 fw-bold h4'>Pending Invoice</h1>
        </Col>
      </Row>

      <div className='flex-grow-1 overflow-hidden' style={{ height: "calc(100vh - 230px)" }}>
        <Card className='h-100 p-3 w-100 '>
          <div className='d-flex flex-column h-100 rounded-0 list-data-grid-wrapper'>
            <DataGrid
              columns={columns}
              data={data}
              loading={loading}
              isFilterApplied={Object.keys(query).some((key) => key !== "page")}
              noDataTitle='No pending invoices found'
              noDataSubtitle='There are no pending invoices available right now.'
              noDataFilteredTitle='No pending invoices match your filters'
              noDataFilteredSubtitle='Try changing or clearing the filter criteria.'
              filterByDate={true}
              FilterSubHeader={true}
              filterData={filterData}
              setFilterData={setFilterData}
              subHeaderComponent={
                <Row className='w-100 align-items-start g-3 py-1 px-1'>
                  <Col md={2}>
                    <FormSelect
                      id='party'
                      name='party'
                      label='Party'
                      isAsync
                      isClearable
                      defaultFilter={{ status: true }}
                      getOptions={getParties}
                      onChange={(name: any, value: any) => {
                        setParty(value);
                        setFilterData({
                          ...filterData,
                          [name]: value?.id,
                        });
                      }}
                      getOptionLabel={(row: any) => `${row.name} (${row.code})`}
                      formatOptionLabel={(row: any, { context }: any) =>
                        context === "menu" ? (
                          <>
                            <small>{row.name}</small>
                            <div>({row.code})</div>
                          </>
                        ) : (
                          <span>
                            {row.name} ({row.code})
                          </span>
                        )
                      }
                      value={party}
                    />
                  </Col>
                  <Col md={2}>
                    <FormInput
                      name='orderNumber'
                      label='Order No.'
                      placeholder='Order No.'
                      onChange={(e) =>
                        setFilterData({
                          ...filterData,
                          [e.target.name]: e.target.value,
                        })
                      }
                      value={filterData.orderNumber}
                    />
                  </Col>

                  <Col md='auto'>
                    <div className='filter-action-buttons'>
                      <Button color='primary' size='sm' onClick={() => handleFilter()}>
                        Filter
                      </Button>
                      <Button color='danger' size='sm' onClick={() => handleClearFilter()}>
                        Clear
                      </Button>
                    </div>
                  </Col>
                </Row>
              }
            />
          </div>
        </Card>
      </div>

      <Modal isOpen={modal} toggle={toggle} size='lg'>
        <ModalHeader toggle={toggle}>Pending Dispatch :- {invoice?.orderNumber}</ModalHeader>

        <Formik
          initialValues={{
            invoiceNumber: "",
            invoiceDate: moment().format("YYYY-MM-DD"),
            invoiceAmount: invoice?.orderAmount,
            checkedNumberOfBox: invoice?.numberOfBox,
            checkedLineItem: invoice?.lineItem,
          }}
          validationSchema={validationSchema(selectedCompany)}
          onSubmit={(values) => {
            updatePendingInvoice(invoice.id, values)
              .then(() => {
                getPendingInvoices({ page: 1 }).then((response: any) => {
                  setData(response.data);
                  toggle();
                });
              })
              .catch(() => {});
          }}
        >
          {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
            <>
              <ModalBody>
                <Row>
                  <Col md={6}>
                    <FormInput
                      label='Invoice Number'
                      name='invoiceNumber'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={getIn(values, "invoiceNumber")}
                      error={getIn(errors, "invoiceNumber")}
                      touched={getIn(touched, "invoiceNumber")}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      label='Invoice Date'
                      name='invoiceDate'
                      type='date'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={getIn(values, "invoiceDate")}
                      error={getIn(errors, "invoiceDate")}
                      touched={getIn(touched, "invoiceDate")}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      label='Invoice Amount'
                      name='invoiceAmount'
                      type='number'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={getIn(values, "invoiceAmount")}
                      error={getIn(errors, "invoiceAmount")}
                      touched={getIn(touched, "invoiceAmount")}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      label='Number of Box'
                      name='checkedNumberOfBox'
                      type='number'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={getIn(values, "checkedNumberOfBox")}
                      error={getIn(errors, "checkedNumberOfBox")}
                      touched={getIn(touched, "checkedNumberOfBox")}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      label='Line Item'
                      name='checkedLineItem'
                      type='number'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={getIn(values, "checkedLineItem")}
                      error={getIn(errors, "checkedLineItem")}
                      touched={getIn(touched, "checkedLineItem")}
                    />
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <Button
                  size='sm'
                  color='primary'
                  onClick={() => {
                    handleSubmit();
                  }}
                >
                  Submit
                </Button>
                <Button size='sm' color='secondary' onClick={toggle}>
                  Cancel
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </Modal>
    </div>
  );
}

export default PendingInvoice;
